var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LinkedAccountsApiService } from '@ClientApi/linkedAccounts';
import { EMBURSE_ISSUED_CARDS, INSTITUTION_TYPE } from '@Shared/constants';
import { AccountCapabilitiesCardAllowanceIntervalEnum, CardType, INTERVAL_LONG_DISPLAY_NAMES } from '@Shared/interfaces/CardsGatewayInterfaces';
export const GATEWAY_PROVIDER = 'emburse_cards_gateway';
// Caching state at the module level is not ideal, but the alternative is wrapping the whole thing
// in a closure or using a class-based approach. Both better IMO but more difficult to pull off
// without breaking something else in the legacy angular app.
const cardProgramCapabilitiesById = new Map();
const observers = [];
// Used to prevent over-fetching when fetching card capabilities for a list of cards.
const cardProgramCapabilitiesIsLoading = new Set();
export function getCardProgramCapabilitiesById() {
    return cardProgramCapabilitiesById;
}
export function getCardProgramCapabilities(cardProgramId) {
    if (!cardProgramId) {
        throw new Error('Invalid card program ID for fetching capabilities');
    }
    return cardProgramCapabilitiesById.get(cardProgramId);
}
/**
 * Fetch the capabilities associated with a card program and cache for later use.
 */
export function fetchCardProgramCapabilities(cardProgramId) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!cardProgramId) {
            throw new Error('Invalid card program ID for fetching capabilities');
        }
        if (!cardProgramCapabilitiesById.has(cardProgramId) && !cardProgramCapabilitiesIsLoading.has(cardProgramId)) {
            cardProgramCapabilitiesIsLoading.add(cardProgramId);
            const capabilities = yield LinkedAccountsApiService.getAccountCapabilities(cardProgramId);
            cardProgramCapabilitiesById.set(cardProgramId, capabilities);
            cardProgramCapabilitiesIsLoading.delete(cardProgramId);
            notifyObservers();
        }
    });
}
/**
 * Primarily used for test purposes.
 */
export function resetCardProgramCapabilitiesMap() {
    cardProgramCapabilitiesById.clear();
    cardProgramCapabilitiesIsLoading.clear();
    notifyObservers();
}
export function isGatewayCardOrProgram(cardOrProgram) {
    return (cardOrProgram === null || cardOrProgram === void 0 ? void 0 : cardOrProgram.provider) === GATEWAY_PROVIDER;
}
export function cardIsGatewayIssuingEnabled(card, programCapabilities) {
    if (!isGatewayCardOrProgram(card)) {
        return false;
    }
    const capabilities = programCapabilities || getCardProgramCapabilities(card.parent_linked_account_id);
    if (!capabilities) {
        return false;
    }
    if (card.is_virtual_card) {
        return capabilities.issuing.virtual;
    }
    else if (card.is_ghost) {
        return capabilities.issuing.ghost;
    }
    return capabilities.issuing.physical;
}
export function cardProgramIsGatewayIssuingEnabled(cardProgram, programCapabilities) {
    if (!isGatewayCardOrProgram(cardProgram)) {
        return false;
    }
    const capabilities = programCapabilities || getCardProgramCapabilities(cardProgram.id);
    if (!capabilities) {
        return false;
    }
    const canIssueVirtualCards = capabilities.issuing.virtual;
    const canIssueRealCards = capabilities.issuing.ghost || capabilities.issuing.physical;
    return canIssueVirtualCards || canIssueRealCards;
}
export function cardSupportsStatusByCapability(card, supportedStatus, programCapabilities) {
    if (!cardIsGatewayIssuingEnabled(card, programCapabilities)) {
        return false;
    }
    const capabilities = programCapabilities || getCardProgramCapabilities(card.parent_linked_account_id);
    if (!capabilities) {
        return false;
    }
    if (card.is_virtual_card) {
        return capabilities.virtualCard.status.some((status) => status.toLowerCase() === supportedStatus.toLowerCase());
    }
    return capabilities.card.status.some((status) => status.toLowerCase() === supportedStatus.toLowerCase());
}
export function registerObserver(observer) {
    observers.push(observer);
}
export function unregisterObserver(observer) {
    observers.splice(observers.indexOf(observer), 1);
}
function notifyObservers() {
    observers.forEach((observer) => observer.update());
}
/**
 * Gets a display value for a given interval
 * @param provider The card's provider
 * @param interval The interval.  Optional.  Will default to "non-recurring" if not provided.
 * @returns
 */
export function getCardIntervalDisplay(provider, interval) {
    if (provider === GATEWAY_PROVIDER) {
        return INTERVAL_LONG_DISPLAY_NAMES[(interval || AccountCapabilitiesCardAllowanceIntervalEnum.Continuous).toLowerCase()];
    }
}
export function getCardTypeDisplay(type, card_type) {
    if (type === 'personal_card') {
        return 'personal';
    }
    return card_type.toLowerCase() === CardType.Physical ? CardType.Physical : CardType.Virtual;
}
export function getCardAllowanceAmount(provider, allowance) {
    if (provider === GATEWAY_PROVIDER) {
        return allowance === null || allowance === void 0 ? void 0 : allowance.intervalAmount;
    }
}
export function getCardAllowanceBalance(provider, allowance) {
    if (provider === GATEWAY_PROVIDER) {
        return allowance === null || allowance === void 0 ? void 0 : allowance.remainingBalance;
    }
}
/**
 * Gets whether a program is issuing-enabled or not.  Unlike @func cardProgramIsGatewayIssuingEnabled, this works for all providers.
 * @param program The program
 * @param capabilities Capabilities.  Optional, and unused if not a Gateway program.  If needed but not provided, will use cached capabilities if available (if not, will return false).
 * @returns
 */
export function canIssueCardsForProgram(program, capabilities) {
    if (isGatewayCardOrProgram(program)) {
        return cardProgramIsGatewayIssuingEnabled(program, capabilities);
    }
    return program.supports_card_issuing;
}
/**
 * Check if any program supports issuing
 *
 * @export
 * @param {ILinkedAccountModel[]} cardPrograms
 * @param {Record<string, AccountCapabilities>} capabilitiesMap
 */
export function hasIssuableCardProgram(cardPrograms, capabilitiesMap) {
    return cardPrograms.some((cardProgram) => {
        const capabilitiesForProgram = capabilitiesMap[cardProgram.id];
        return capabilitiesForProgram && canIssueCardsForProgram(cardProgram, capabilitiesForProgram);
    });
}
export function isAmexGatewayProgram(card) {
    if ([INSTITUTION_TYPE.AMEX_CPG, INSTITUTION_TYPE.AMEX_FINS, INSTITUTION_TYPE.AMEX_FILES].includes(card.institution_type)) {
        return true;
    }
    return false;
}
/**
 * Gets whether a card is supported by the card details modal or page
 * @param card The card
 * @param capabilities Capabilities.  Optional, and unused if not a Gateway card.  If needed but not provided, will use cached capabilities if available (if not, will return false).
 * @returns
 */
export function canViewDetailsForCard(card, capabilities) {
    if (isGatewayCardOrProgram(card)) {
        if (isAmexGatewayProgram(card)) {
            return true;
        }
        return cardIsGatewayIssuingEnabled(card, capabilities);
    }
    return card.type === 'personal_card' && window.FEATURES.embark_personal_cards;
}
export function isEmburseIssuedCard(issuingDetails) {
    return isGatewayCardOrProgram(issuingDetails) && EMBURSE_ISSUED_CARDS.includes(issuingDetails.institution_type);
}
